import {mapActions, mapState, mapGetters} from 'vuex';
import Multiselect from 'vue-multiselect';
import _ from 'lodash';
import {SETTLEMENT_CONFIRMATION_MODAL_EVENT} from '../../common/constants/constants';

const SEARCH_QUERY_MIN_LENGTH = 2;
const REFS = {
	SETTLEMENT_MULTISELECT: '',
	STREET_MULTISELECT: 'streetSelector',
	HOUSE_NUMBER_INPUT: 'houseNumberSelector',
	SEND_BUTTON: 'settlementSendButton'
};
const STATES = {
	SET_ADDRESS: 'SET_ADDRESS',
	SET_OFFLINE_ADDRESS: 'SET_OFFLINE_ADDRESS',
	SHOW_ADDRESS: 'SHOW_ADDRESS',
	NO_ONLINE_OFFER: 'NO_ONLINE_OFFER'
};
const EMPTY_OFFLINE_ADDRESS = {
	settlement: '',
	isSettlementSet: false,
	etc: ''
};

export default {
	data() {
		return {
			state: STATES.SET_ADDRESS,
			settlementSearchValue: '',
			streetSearchValue: '',
			houseNumberSearchValue: '',
			choosenServices: [],
			actionsShownOnMobile: false,
			offlineAddress: {...EMPTY_OFFLINE_ADDRESS},
			isOffline: false,
			offlineOfferIsShown: true,
			isSearch: false,
			openDirection: 'bottom',
			redirectTarget: window.settlementRedirectTo || this.redirectTo,
			createNewAddress: true,
			addressIdsFromUrl: ''
		};
	},
	props: {
		redirectTo: {
			type: String,
			required: false
		}
	},
	components: {
		Multiselect
	},
	computed: {
		...mapState('settlementStore', [
			'settlements',
			'settlement',
			'streets',
			'street',
			'houseNumbers',
			'houseNumber'
		]),
		...mapState('concernModalStore', ['visible']),
		...mapGetters('cartsStore', ['addresses', 'activeCartIndex', 'activeAddress']),

		REFS() {
			return REFS;
		},
		STATES() {
			return STATES;
		},
		isMobile() {
			return this.$resize && this.$mq.expr(this.$mq.bands.mobile);
		},
		showDetailText() {
			return this.isOffline ? !this.offlineAddress.isSettlementSet : !Object.keys(this.settlement).length;
		},
		actionsShown() {
			return this.isMobile ? this.actionsShownOnMobile : true;
		},
		displayedAddress() {
			if (this.isOffline) {
				return `${this.offlineAddress.settlement}, ${this.offlineAddress.etc}.`;
			} else {
				return this.activeAddress ? this.activeAddress.label : '';
			}
		},
		showAddress() {
			return this.state == STATES.SHOW_ADDRESS || this.isOffline && this.state == STATES.NO_ONLINE_OFFER;
		},
		hasMultipleAddress() {
			return this.addresses.length > 1;
		},
		saveButtonDisabled() {
			return !this.houseNumberSearchValue || !this.streetSearchValue || !this.settlementSearchValue;
		},
		showSelectLabels: {
			get: function () {
				return this.isSearch;
			},
			set: function (newValue) {
				this.isSearch = newValue;
			}
		}
	},
	watch: {
		activeAddress: {
			immediate: true,
			handler(newAddress) {
				if (newAddress) {
					this.state = STATES.SHOW_ADDRESS;
				}
			}
		}
	},
	created() {
		window.EventBus.$on(SETTLEMENT_CONFIRMATION_MODAL_EVENT.CONFIRM, this.changeAddressAndSetState);
		window.EventBus.$on(SETTLEMENT_CONFIRMATION_MODAL_EVENT.CANCEL, this.onCancelAddressChange);

		this.addressIdsFromUrl = this.creatingAddressDataFromUrl();

		if (Object.keys(this.addressIdsFromUrl).length) {
			this.addressSettlement().then(() => {
				this.createNewCartWithAddress();
			});
		}
	},
	methods: {
		...mapActions('settlementStore', [
			'changeActiveAddress',
			'changeAddress',
			'createCartWithAddress',
			'getAddressesOnSettlement',
			'getStreetsByTelepulesId',
			'resetAddress',
			'setHouseNumber',
			'setSettlement',
			'setStreet'
		]),
		...mapActions('concernModalStore', ['toggleModal']),

		async onSettlementSelect(selectedOption) {
			await this.setSettlement(selectedOption);
			this.streetSearchValue = '';
			this.houseNumberSearchValue = '';
			await this.getStreetsByTelepulesId();
			await this.$nextTick(() => {
				this.$refs[REFS.STREET_MULTISELECT].$refs.search.focus();
			});
		},

		async onStreetSelect(selectedOption) {
			await this.setStreet(selectedOption);
			this.houseNumberSearchValue = '';
			await this.$nextTick(() => {
				this.$refs[REFS.HOUSE_NUMBER_INPUT].focus();
			});
		},

		onHouseNumberSelect() {
			this.$nextTick(() => {
				this.$refs[REFS.SEND_BUTTON].focus();
			});
		},

		settlementSearch: _.debounce(function (value) {
			this.showSelectLabels = value.length >= SEARCH_QUERY_MIN_LENGTH;

			if (this.showSelectLabels) {
				this.getAddressesOnSettlement(value);
			}
		}, 150),

		settlementNameAndZipCode({nev, irsz}) {
			return `${nev}, ${irsz}`;
		},

		streetName({utca}) {
			return `${utca}`;
		},

		preSelectSettlement(telepulesNev) {
			return this.getAddressesOnSettlement(telepulesNev)
				.then(() => {
					this.settlementSearchValue = this.settlements[0];
					this.setSettlement(this.settlementSearchValue);
				});
		},

		preSelectStreet(utca) {
			return this.getStreetsByTelepulesId()
				.then(() => {
					this.streetSearchValue = this.streets.find(street => street.utca == utca);
					this.setStreet(this.streetSearchValue);
				});
		},

		preSelectHouseNumber(hazSzam) {
			this.houseNumberSearchValue = hazSzam;
			this.setHouseNumber(this.houseNumberSearchValue);
		},

		async onCancelAddressChange() {
			await this.preSelectAddress();
			this.state = STATES.SHOW_ADDRESS;
		},

		async preSelectAddress() {
			let settlement = null;
			let street = null;
			let houseNumber = null;

			if (this.isOffline) {
				const etcSplit = /(.*\w+)(\s+\d+)/;
				const match = this.offlineAddress.etc.match(etcSplit);

				settlement = this.offlineAddress.settlement;
				street = match[1].trim();
				houseNumber = match[2].trim();
			} else {
				settlement = this.activeAddress.telepulesNev;
				street = this.activeAddress.utca;
				houseNumber = this.activeAddress.hazSzam;
			}

			this.isOffline = false;
			this.offlineOfferIsShown = false;
			await this.preSelectSettlement(settlement);
			await this.preSelectStreet(street);
			await this.preSelectHouseNumber(houseNumber);
		},

		changeAddressAndSetState(byUser = null) {
			this.setHouseNumber(this.houseNumberSearchValue);
			this.changeAddress().then(() => {
				this.state = STATES.SHOW_ADDRESS;
				if (byUser && window.cPNiceUrl) {
					window.location.href = window.cPNiceUrl;
				}
			}).catch(error => {
				if (error) {
					this.state = STATES.SHOW_ADDRESS;
				} else {
					this.offlineAddress.settlement = this.settlementSearchValue.nev;
					this.offlineAddress.etc = `${this.streetSearchValue.utca} ${this.houseNumberSearchValue}`;
					this.showOfflineAddress();
				}
			});
		},

		createNewCartWithAddress() {
			this.setHouseNumber(this.houseNumberSearchValue);
			this.createCartWithAddress()
				.then(() => {
					this.state = STATES.SHOW_ADDRESS;
					this.createNewAddress = false;
				})
				.catch(() => {
					this.offlineAddress.settlement = this.settlementSearchValue.nev;
					this.offlineAddress.etc = `${this.streetSearchValue.utca} ${this.houseNumberSearchValue}`;
					this.showOfflineAddress();
				});
		},

		modifyCurrentAddress() {
			this.toggleActionsOnMobile();
			if (this.redirectTarget) {
				window.location.href = this.redirectTarget;
			}

			this.createNewAddress = false;

			this.state = STATES.SET_ADDRESS;
			this.preSelectAddress();
		},

		setNewAddress() {
			this.toggleActionsOnMobile();
			if (this.redirectTarget) {
				window.location.href = this.redirectTarget;
			}

			this.createNewAddress = true;
			this.offlineAddress = {...EMPTY_OFFLINE_ADDRESS};
			this.state = STATES.SET_ADDRESS;
			this.settlementSearchValue = '';
			this.streetSearchValue = '';
			this.houseNumberSearchValue = '';
			this.isOffline = false;

			this.resetAddress();
		},

		toggleActionsOnMobile() {
			if (this.isMobile) {
				this.actionsShownOnMobile = !this.actionsShownOnMobile;
			}
		},

		showOfflineOffer() {
			this.offlineOfferIsShown = true;
			this.state = STATES.NO_ONLINE_OFFER;
		},

		showOfflineAddressSet() {
			this.state = STATES.SET_OFFLINE_ADDRESS;
			this.isOffline = true;
			this.offlineOfferIsShown = true;
		},

		showOfflineAddress() {
			this.state = STATES.NO_ONLINE_OFFER;
			this.isOffline = true;
			this.offlineOfferIsShown = true;
		},

		setOfflineSettlement() {
			if (this.offlineAddress.settlement) {
				this.offlineAddress.isSettlementSet = true;
			}
		},

		closeOfflineOffer() {
			this.offlineOfferIsShown = false;
		},

		openConcernModal() {
			this.toggleModal({
				displayedAddress: this.displayedAddress,
				displayedChoosenServices: this.choosenServices.join(', ')
			});
		},
		creatingAddressDataFromUrl() {
			const url = window.location.href;
			let addressDetails = {};

			if (url.includes('?')) {
				let literalAddressDetails = url.slice(url.indexOf('?') + 1);
				const removedAndsignDetails = literalAddressDetails.split("&");

				let removedEqualsignDetails = [];
				removedAndsignDetails.forEach((element) => {
					removedEqualsignDetails.push(element.split("="));
				});

				for(let i = 0; i < removedEqualsignDetails.length; i++) {
					addressDetails[(removedEqualsignDetails[i])[0]] = decodeURIComponent((removedEqualsignDetails[i])[1]);
				}

				this.houseNumberSearchValue = addressDetails.houseNumber;
			}
			return addressDetails;
		},

		async addressSettlement() {
			const townAndAddress = this.addressIdsFromUrl.settlement.split(',');
			const postalCodeAndTown = townAndAddress[0];
			const town = postalCodeAndTown.split(" ");

			const addressInSettlement =  townAndAddress[1].slice(1);
			const lastWhiteSpaceIndex = this.whitespaceIndex(addressInSettlement);

			const street = addressInSettlement.slice(0, lastWhiteSpaceIndex - 1);
			const houseNumber = addressInSettlement.slice(lastWhiteSpaceIndex, addressInSettlement.length - 1);

			await this.preSelectSettlement(town[1]);
			await this.preSelectStreet(street);
			await this.preSelectHouseNumber(houseNumber);
		},

		whitespaceIndex(stringLiteral) {
			let whitespaceIndex = 0;
			if (stringLiteral.length) {
				for(let i = stringLiteral.length - 1; i >= 0 && stringLiteral[i] != ' '; i--) {
					whitespaceIndex = i;
				}
			}
			return whitespaceIndex;
		}
	}
};



// WEBPACK FOOTER //
// ../source/modules/SET01-settlement/SET01-settlement.js